import type { FC, ReactNode } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { LogOut01 } from '@untitledui/icons/LogOut01';

import type { User } from '@/utils/session/fetch-user';

import { AvatarCard } from '@/components/avatar-card/avatar-card';
import { BASE_PATH } from '@/constants/config';
import { useTheme } from '@/providers/theme-provider';
import { cx } from '@/utils/cx';

import { MobileNavigationHeader } from './base-components/mobile-header';
import { NavDropdownItem } from './base-components/nav-dropdown-item';
import { NavItemBase } from './base-components/nav-item';
import { NavList } from './base-components/nav-list';

export type NavItem = {
  /** Label text for the nav item. */
  label: string;
  /** URL to navigate to when the nav item is clicked. */
  href: string;
  /** Whether the nav item is currently active. */
  current?: boolean;
  /** Icon component to display. */
  icon?: FC<{ className?: string }>;
  /** Badge to display. */
  badge?: ReactNode;
  /** List of sub-items to display. */
  items?: (NavItem | { divider: true; label?: string })[];
  /** Test ID for the nav item (for testing purposes). */
  testId?: string;
};

interface HeaderNavigationBaseProps {
  /** URL of the currently active item. */
  activeUrl?: string;
  /** List of items to display. */
  items: NavItem[];
  /** List of sub-items to display. */
  subItems?: NavItem[];
  /** Content to display in the trailing position. */
  trailingContent?: ReactNode;
  /** Whether to show the avatar dropdown. */
  showAvatarDropdown?: boolean;
  /** Whether to hide the bottom border. */
  hideBorder?: boolean;
  /** User data for avatar display. */
  user?: User | null;
  /** Override the logo link destination. Defaults to BASE_PATH. */
  logoHref?: string;
}

export const HeaderNavigationBase = ({
  items,
  hideBorder = false,
  user,
  logoHref = BASE_PATH,
}: HeaderNavigationBaseProps) => {
  const { resolvedTheme } = useTheme();
  const { t } = useLingui();

  const logoSrc =
    resolvedTheme === 'dark'
      ? `${BASE_PATH}/logo-light.svg`
      : `${BASE_PATH}/logo-dark.svg`;

  // Filter out Sign out from Settings subitems for mobile (it's shown at the bottom)
  const mobileItems = items.map((item) => {
    if (item.items) {
      // Remove Sign out link
      const filteredItems = item.items.filter(
        (subItem) =>
          'divider' in subItem || subItem.href !== `${BASE_PATH}/sign-out`,
      );
      // Remove trailing dividers
      while (
        filteredItems.length > 0 &&
        // Using not-null assertion because we know if length is not 0, there will be an item
        // oxlint-disable-next-line typescript/no-non-null-assertion
        'divider' in filteredItems.at(-1)!
      ) {
        filteredItems.pop();
      }
      return { ...item, items: filteredItems };
    }
    return item;
  });

  return (
    <>
      <MobileNavigationHeader logoHref={logoHref} showBurgerMenu={!!user}>
        <aside className="flex h-full max-w-full flex-col justify-between overflow-auto border-r border-secondary bg-primary pt-4 lg:pt-6">
          <div className="flex flex-col items-start gap-5 px-4 lg:px-5">
            <a
              aria-label={t`Go to homepage`}
              href={logoHref}
              className="rounded-xs outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              <img src={logoSrc} alt="Respondent" className="h-6" />
            </a>
          </div>

          <NavList items={mobileItems} />

          <div className="mt-auto flex flex-col gap-4 px-2 py-4 lg:px-4 lg:py-6">
            <AvatarCard size="md" user={user} />

            <NavItemBase
              type="link"
              href={`${BASE_PATH}/sign-out`}
              icon={LogOut01}
            >
              <Trans>Sign out</Trans>
            </NavItemBase>
          </div>
        </aside>
      </MobileNavigationHeader>

      <header className="sticky top-0 z-50 max-lg:hidden">
        <section
          className={cx(
            'flex h-16 w-full items-center justify-center bg-primary md:h-18',
            !hideBorder && 'border-b border-secondary',
          )}
        >
          <div className="flex w-full max-w-container justify-between pr-3 pl-4 md:px-8">
            <div className="flex w-full flex-1 items-center gap-4">
              <a
                aria-label="Go to homepage"
                href={logoHref}
                className="rounded-xs outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2"
              >
                <img src={logoSrc} alt="Respondent" className="h-8" />
              </a>

              <nav className="w-full">
                <ul className="flex w-full items-end justify-end gap-0.5">
                  {items.map((item) => {
                    if (item.items) {
                      return (
                        <li
                          key={item.label}
                          className="py-0.5"
                          data-testid={item.testId}
                        >
                          <NavDropdownItem
                            items={item.items}
                            icon={item.icon}
                            href={item.href}
                            current={item.current}
                            badge={item.badge}
                            user={user}
                          >
                            {item.label}
                          </NavDropdownItem>
                        </li>
                      );
                    }
                    return (
                      <li
                        key={item.label}
                        className="py-0.5"
                        data-testid={item.testId}
                      >
                        <NavItemBase
                          icon={item.icon}
                          href={item.href}
                          current={item.current}
                          badge={item.badge}
                          type="link"
                        >
                          {item.label}
                        </NavItemBase>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>
          </div>
        </section>
      </header>
    </>
  );
};
